import { useQuery } from '@tanstack/react-query';
import { Wallet, Calendar, CheckCircle, Clock, Car } from 'lucide-react';
import { api } from '@/lib/api';
import { formatPriceWithCurrency, formatDate, VEHICLE_LABELS } from '@/lib/utils';
import { Spinner } from '@/components/Spinner';
import EmptyState from '@/components/EmptyState';

interface SettlementItem {
  vehicleType: string;
  washCount: number;
  ratePerWash: number;
  totalAmount: number;
}

interface Settlement {
  id: string;
  periodStart: string;
  periodEnd: string;
  totalWashes: number;
  totalAmount: number;
  status: 'pending' | 'paid';
  paidAt: string | null;
  paidReference: string | null;
  items: SettlementItem[];
}

interface SettlementsData {
  settlements: Settlement[];
  summary: {
    totalPaid: number;
    totalPending: number;
    totalWashes: number;
  };
}

export default function SettlementsPage() {
  const { data, isLoading } = useQuery<SettlementsData>({
    queryKey: ['owner', 'settlements'],
    queryFn: () => api.get('/owner/settlements'),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  const { settlements = [], summary = { totalPaid: 0, totalPending: 0, totalWashes: 0 } } = data || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-gray-900">التسويات</h1>
        <p className="text-gray-500 text-sm mt-1">سجل المدفوعات الشهرية</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-2xl p-4 shadow-card text-center">
          <Wallet className="w-6 h-6 text-green-600 mx-auto mb-1" />
          <p className="text-xl font-bold text-gray-900">{formatPriceWithCurrency(summary.totalPaid)}</p>
          <p className="text-xs text-gray-500">تم الدفع</p>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-card text-center">
          <Clock className="w-6 h-6 text-amber-600 mx-auto mb-1" />
          <p className="text-xl font-bold text-gray-900">{formatPriceWithCurrency(summary.totalPending)}</p>
          <p className="text-xs text-gray-500">معلق</p>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-card text-center">
          <Car className="w-6 h-6 text-blue-600 mx-auto mb-1" />
          <p className="text-2xl font-bold text-gray-900">{summary.totalWashes}</p>
          <p className="text-xs text-gray-500">إجمالي الغسلات</p>
        </div>
      </div>

      {/* Settlements List */}
      {settlements.length === 0 ? (
        <EmptyState icon={Wallet} title="لا توجد تسويات" description="ستظهر التسويات الشهرية هنا" />
      ) : (
        <div className="space-y-4">
          {settlements.map((settlement) => (
            <div key={settlement.id} className="bg-white rounded-2xl shadow-card overflow-hidden">
              {/* Header */}
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    settlement.status === 'paid' ? 'bg-green-100' : 'bg-amber-100'
                  }`}>
                    {settlement.status === 'paid' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <Clock className="w-5 h-5 text-amber-600" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5 text-gray-400" />
                      <p className="text-sm text-gray-600">
                        {formatDate(settlement.periodStart)} - {formatDate(settlement.periodEnd)}
                      </p>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {settlement.totalWashes} غسلة
                    </p>
                  </div>
                </div>
                <div className="text-left">
                  <p className="text-lg font-bold text-gray-900">
                    {formatPriceWithCurrency(settlement.totalAmount)}
                  </p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    settlement.status === 'paid'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {settlement.status === 'paid' ? 'مدفوع' : 'معلق'}
                  </span>
                </div>
              </div>

              {/* Items Breakdown */}
              {settlement.items && settlement.items.length > 0 && (
                <div className="border-t border-gray-100 px-4 py-3 bg-gray-50/50">
                  <div className="space-y-2">
                    {settlement.items.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">
                          {VEHICLE_LABELS[item.vehicleType]?.ar} ({item.washCount} غسلة)
                        </span>
                        <span className="text-gray-900 font-medium">
                          {formatPriceWithCurrency(item.totalAmount)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Payment Info */}
              {settlement.paidAt && (
                <div className="border-t border-gray-100 px-4 py-2 bg-green-50/50">
                  <p className="text-xs text-green-700">
                    تم الدفع: {formatDate(settlement.paidAt)}
                    {settlement.paidReference && ` | رقم المرجع: ${settlement.paidReference}`}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
